import { FeaturesApiClient } from '@enara-modules/enara-module-features-api-client';
import api from 'api';
import EnaraLoader from 'components/elements/loaders/enara-loader/EnaraLoader';
import Toast from 'components/toast';
import { FEATURE } from 'config';
import { DEFAULT_FEATURES, EFeatures } from 'lib/feature/features.types';
import node_api from 'node-api';
import { ReviewListClient } from 'node-api/reviewList/reviewlistClient';
import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { MixpanelWrapperInstance } from 'services/mixpanel/mixpanel-wrapper';
import { RpmEventTrackingService } from 'services/rpm/rpm-track-event-service';
import { hasUsableSession, sessionMonitor } from 'services/session';
import { signOut } from 'services/sign-out';
import { ClinicContext } from 'state/contexts/clinic';
import { FeaturesContext } from 'state/contexts/features';
import { ProgramContext } from 'state/contexts/program';
import { ProvidersContext } from 'state/contexts/providers';
import { ReviewLists4Context } from 'state/contexts/reviewLists4';
import { StatusContext } from 'state/contexts/status';
import { TagContext } from 'state/contexts/tag';
import { UserContext } from 'state/contexts/user';
import { ActionTypes as FeaturesActionTypes } from 'state/reducers/features';
import { ActionTypes } from 'state/reducers/providers';
import { ActionTypes as UserActionTypes } from 'state/reducers/user';
import { getOriginalUserUUID, getProviderClinicIds } from 'utils/helpers';
import BootstrapError from './BootstrapError';
import { decideBootstrapFailure, describeFailure } from './bootstrapFailure';
import { FullPageWrapperForLoader, LoaderContainer } from './style';

// Returned by a fetcher that failed, so the boot effect can tell it from data.
const BOOTSTRAP_FAILED = Symbol('bootstrap-failed');

const Authentication = ({ history, children }) => {
  const { userState, userDispatch } = useContext(UserContext);
  const { programDispatch } = useContext(ProgramContext);
  const { statusDispatch } = useContext(StatusContext);
  const { tagDispatch } = useContext(TagContext);
  const { clinicDispatch } = useContext(ClinicContext);
  const { providersDispatch } = useContext(ProvidersContext);
  const { featuresDispatch } = useContext(FeaturesContext);
  const { reviewLists4Dispatch } = useContext(ReviewLists4Context);

  const [loading, setLoading] = useState(true);
  const [bootstrapError, setBootstrapError] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // Snapshot at mount: signing in or out navigates, which remounts this.
  const [isLogged] = useState(() => hasUsableSession());
  const signedOutRef = useRef(false);

  const logoutProviderAndReportError = useCallback(
    (error) => {
      // Boot requests fail in parallel; sign out once.
      if (signedOutRef.current) {
        return;
      }
      signedOutRef.current = true;
      signOut();
      history.push('/login');
      Toast.show('error', error);
    },
    [history]
  );

  // Only a failure that looks like a rejected token may sign out, and only in
  // report-only mode; with enforcement on the session guard owns the sign-out.
  // Anything else (Core down, 5xx, network) keeps the user signed in.
  const handleBootstrapFailure = useCallback(
    (name, err, message) => {
      console.error(`[bootstrap] ${name} failed`, describeFailure(err));
      if (decideBootstrapFailure(err, sessionMonitor.isEnforcing()) === 'sign-out') {
        logoutProviderAndReportError(message);
      } else {
        Toast.show('error', message, { toastId: 'bootstrap-failure' });
      }
      return BOOTSTRAP_FAILED;
    },
    [logoutProviderAndReportError]
  );

  const getFeatures = useCallback(
    async (clinics) => {
      const featuresClient = new FeaturesApiClient({
        targetEnv: FEATURE.targetEnv,
        baseUrl: FEATURE.url,
      });

      let featuresResponse = null;

      try {
        featuresResponse = await featuresClient.getFeatures({
          useRawValues: true,
          featureNames: Object.values(EFeatures),
        });
      } catch (err) {
        console.error('Error getting feature flags. Using defaults');
      }

      const featuresToDispatch = { ...DEFAULT_FEATURES };
      if (clinics && featuresResponse) {
        const providerClinicIds = getProviderClinicIds(clinics);
        Object.entries(featuresResponse.values).forEach(([key, { enabled, clinicIds }]) => {
          featuresToDispatch[key] =
            enabled &&
            (!clinicIds || clinicIds.some((clinicId) => providerClinicIds.has(clinicId)));
        });
      }

      featuresDispatch({
        type: FeaturesActionTypes.SET,
        payload: featuresToDispatch,
      });

      return featuresToDispatch;
    },
    [featuresDispatch]
  );

  const fetchUserData = useCallback(async () => {
    // `getOriginalUserUUID()` already neutralizes and evicts the legacy
    // `'undefined'` sentinel, so a truthy value here is a real impersonator.
    const originalUserUUID = getOriginalUserUUID();
    const isOriginalUser = !originalUserUUID;

    try {
      const {
        data: { user },
      } = await api().get('users/AUTH_ID', {
        params: isOriginalUser
          ? { auth_token: 'AUTH_TOKEN' }
          : { auth_token: 'AUTH_TOKEN', original_user_uuid: originalUserUUID },
      });

      const userDataToDispatch = { ...user, isOriginalUser };
      const currentRoleFound = userDataToDispatch.original_user.available_logins.find((login) => {
        return login.uuid === userDataToDispatch.uuid;
      });

      const roleName = currentRoleFound
        ? `${currentRoleFound.first_name} ${currentRoleFound.last_name}`
        : userDataToDispatch.clinical_role?.name;

      RpmEventTrackingService.setDefaultValues({
        providerUuid: userDataToDispatch.uuid,
        role: roleName ?? 'unknown',
      });

      userDispatch({
        type: UserActionTypes.SET,
        payload: userDataToDispatch,
      });
      return userDataToDispatch;
    } catch (err) {
      return handleBootstrapFailure('fetchUserData', err, "Couldn't get user profile");
    }
  }, [handleBootstrapFailure, userDispatch]);

  const fetchUserPrograms = useCallback(async () => {
    try {
      const {
        data: { data: programs },
      } = await api().get('user_programs/AUTH_TOKEN');

      programDispatch({
        type: 'SET',
        payload: programs,
      });
      return programs;
    } catch (err) {
      return handleBootstrapFailure('fetchUserPrograms', err, "Couldn't get user programs");
    }
  }, [handleBootstrapFailure, programDispatch]);

  const fetchUserStatuses = useCallback(async () => {
    try {
      const {
        data: { data: statuses },
      } = await api().get('profile_statuses?auth_token=AUTH_TOKEN');

      statusDispatch({
        type: 'SET',
        payload: statuses,
      });
      return statuses;
    } catch (err) {
      return handleBootstrapFailure('fetchUserStatuses', err, "Couldn't get user statuses");
    }
  }, [handleBootstrapFailure, statusDispatch]);

  const fetchUserTags = useCallback(async () => {
    try {
      const {
        data: { data: tags },
      } = await api().get('patient_tags?auth_token=AUTH_TOKEN');
      tagDispatch({
        type: 'SET',
        payload: tags,
      });
      return tags;
    } catch (err) {
      return handleBootstrapFailure('fetchUserTags', err, "Couldn't not get user tags");
    }
  }, [handleBootstrapFailure, tagDispatch]);

  const fetchAllProviders = useCallback(async () => {
    try {
      const {
        data: { data: providers },
      } = await api().get('providers/AUTH_TOKEN');

      providersDispatch({
        type: ActionTypes.SET,
        payload: {
          data: providers,
        },
      });
    } catch (err) {
      return handleBootstrapFailure('fetchAllProviders', err, "Couldn't get providers");
    }
  }, [handleBootstrapFailure, providersDispatch]);

  const fetchAssignedReviewLists = useCallback(async () => {
    try {
      const data = await new ReviewListClient().getReviewLists4();
      if (data.success) {
        reviewLists4Dispatch({
          type: 'SET',
          payload: data.reviewLists,
        });
        return data.reviewLists;
      }
    } catch (err) {
      console.error('[bootstrap] fetchAssignedReviewLists failed', describeFailure(err));
      Toast.show('error', "Couldn't get assigned review lists", { toastId: 'bootstrap-failure' });
    }
  }, [reviewLists4Dispatch]);

  const fetchClinics = useCallback(async () => {
    try {
      const {
        data: { data: clinicsAndRoles },
      } = await node_api().get('clinics', {
        params: {
          auth_token: 'AUTH_TOKEN',
        },
      });

      const clinicsAssignedToProvider = clinicsAndRoles.clinics.filter((clinic) => {
        return clinic.assigned;
      });

      clinicDispatch({
        type: 'SET',
        payload: { ...clinicsAndRoles, clinicsAssignedToProvider },
      });
      return clinicsAndRoles;
    } catch (err) {
      return handleBootstrapFailure('fetchClinics', err, "Couldn't get clinics");
    }
  }, [handleBootstrapFailure, clinicDispatch]);

  const retry = useCallback(() => {
    setRetrying(true);
    setAttempt((current) => current + 1);
  }, []);

  const signOutFromError = useCallback(() => {
    signOut();
    history.replace('/login');
  }, [history]);

  // The whole boot, re-run by Retry. Only the user profile and the clinics are
  // blocking: without them the app cannot render, so their failure shows
  // BootstrapError instead of children (unless it already signed out).
  useEffect(() => {
    if (!isLogged) {
      // The session guard may already have sent an expired session there, with
      // the notice in the location state: replacing it again would drop it.
      if (history.location?.pathname !== '/login') {
        history.replace('/login');
      }
      return undefined;
    }

    let cancelled = false;
    const isBlockingFailure = (result) => result === BOOTSTRAP_FAILED && !signedOutRef.current;
    const showBlockingFailure = (result) => {
      const failed = isBlockingFailure(result);
      if (failed && !cancelled) {
        setBootstrapError(true);
      }
      return failed;
    };

    sessionMonitor.verifyIfStale();

    const loadUser = fetchUserData().then(showBlockingFailure);
    const loadClinicsAndFeatures = fetchClinics().then(async (clinics) => {
      const failed = showBlockingFailure(clinics);
      // if clinics failed then getFeatures sets default feature flags
      await getFeatures(clinics === BOOTSTRAP_FAILED ? undefined : clinics);
      return failed;
    });
    fetchUserPrograms();
    fetchUserStatuses();
    fetchUserTags();
    fetchAllProviders();
    fetchAssignedReviewLists();

    Promise.all([loadUser, loadClinicsAndFeatures]).then((failures) => {
      if (cancelled) {
        return;
      }
      setBootstrapError(failures.some(Boolean));
      setRetrying(false);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [attempt]);

  useEffect(() => {
    if (userState) {
      MixpanelWrapperInstance.configureMixpanel(userState);
    }
  }, [userState]);

  if (bootstrapError) {
    return <BootstrapError onRetry={retry} onSignOut={signOutFromError} retrying={retrying} />;
  }

  if (loading) {
    return (
      <FullPageWrapperForLoader>
        <LoaderContainer>
          <EnaraLoader />
        </LoaderContainer>
      </FullPageWrapperForLoader>
    );
  }

  return children;
};

Authentication.propTypes = {
  history: PropTypes.shape().isRequired,
  children: PropTypes.PropTypes.shape().isRequired,
};

export default Authentication;
